import { Container, Group, Stack, TextInput } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { IconSearch, IconSortDescending2 } from "@tabler/icons-react"
import type { CountryIso3166 } from "@tfg/db"
import {
  CountrySelector,
  DataFetchWrapper,
  ExportButton,
  ExportType,
  InfoBox,
  SectionHeader,
  Select,
} from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  supplierMonitoringFiltersValidationType,
  SupplierTypeValidationType,
} from "@tfg/types/schemas/organization/schemas"

import { i18n } from "~/i18n.ts"
import { SupplierMonitoring } from "~/pages/Brand/Suppliers/List/SupplierMonitoring.tsx"
import {
  useListSuppliersCountriesQuery,
  useListSuppliersQuery,
} from "~/pages/Brand/Suppliers/List/supplierHooks.ts"
import { isSort } from "~/pages/Brand/Suppliers/List/utils.ts"
import { useDownloadSuppliersCSVMutation } from "~/pages/Brand/Suppliers/hooks.ts"
import { useListSubSuppliersQuery } from "~/pages/SupplierPortal/SubSupplierMonitoring/hooks/useListSubSuppliersQuery.ts"
import { COUNTRIES } from "~/utils/countries.ts"

export const SUPPLIER_SORT_OPTIONS = [
  {
    label: i18n.t("suppliers:sort.nameAsc"),
    value: "name-asc",
  },
  {
    label: i18n.t("suppliers:sort.nameDesc"),
    value: "name-desc",
  },
  {
    label: i18n.t("suppliers:sort.requestsAsc"),
    value: "requests-asc",
  },
  {
    label: i18n.t("suppliers:sort.requestsDesc"),
    value: "requests-desc",
  },
]

export const SUPPLIER_DIRECT_OPTIONS = [
  { label: i18n.t("suppliers:filter.all"), value: "all" },
  { label: i18n.t("suppliers:filter.direct"), value: "direct" },
  {
    label: i18n.t("suppliers:filter.nonConfidential"),
    value: "indirect",
  },
  {
    label: i18n.t("suppliers:filter.indirectConfidential"),
    value: "indirectConfidential",
  },
]

export type SortField = "name" | "requests"
export type Sort = `${SortField}-${"asc" | "desc"}`

export const PAGE_SIZE = 10

const hooks = {
  supplier: {
    getList: useListSuppliersQuery,
    countries: useListSuppliersCountriesQuery,
  },
  subSupplier: {
    getList: useListSubSuppliersQuery,
    countries: () => ({ data: undefined }),
  },
}

type SupplierMonitoringWrapperProps = {
  type: "supplier" | "subSupplier"
}

export const SupplierMonitoringWrapper = ({
  type,
}: SupplierMonitoringWrapperProps) => {
  const { t } = useTranslation(["suppliers", "common"])
  const [sort, setSort] = useState<Sort>("name-asc")
  const [pagination, setPagination] = useState<number>(1)
  const fetchDataOptions = {
    pageIndex: pagination - 1,
    pageSize: PAGE_SIZE,
  }
  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [debouncedSupplier] = useDebouncedValue<string>(searchSupplier, 600)

  const suppliersCountriesQuery = hooks[type].countries()
  const suppliersCountries = suppliersCountriesQuery.data ?? undefined

  const [filtersValue, setFiltersValue] =
    useState<supplierMonitoringFiltersValidationType>({
      supplierType: undefined,
      country: undefined,
    })

  const suppliersQuery = hooks[type].getList({
    fetchDataOptions,
    searchSupplier: debouncedSupplier,
    filters: filtersValue,
    orderBy: {
      column: sort.split("-")[0] as SortField,
      direction: sort.split("-")[1] as "asc" | "desc",
    },
  })
  useEffect(() => {
    setPagination(1)
  }, [debouncedSupplier, filtersValue])

  const { mutate: downloadMutation, isLoading: isSuppliersCsvDownloading } =
    useDownloadSuppliersCSVMutation({
      searchSupplier,
      filters: filtersValue,
    })

  return (
    <Stack>
      <Stack>
        <SectionHeader
          title={t("monitoring.header")}
          mainCta={
            type === "supplier" && (
              <ExportButton<void>
                label={t("common:actionButtons.export")}
                exportOptions={[
                  {
                    type: ExportType.CSV,
                    label: t("common:actionButtons.exportAsCsv"),
                    exportFunction: downloadMutation,
                    isLoading: isSuppliersCsvDownloading,
                  },
                ]}
              />
            )
          }
        />
        <Group align="flex-end" gap="xs">
          <TextInput
            label={t("list.searchLabel")}
            value={searchSupplier}
            autoFocus
            onChange={(e) => setSearchSupplier(e.target.value)}
            w={180}
            leftSection={<IconSearch size={16} />}
            placeholder={t("list.searchPlaceholder")}
          />
          <Select
            label={t("list.supplierTypeLabel")}
            data={SUPPLIER_DIRECT_OPTIONS}
            placeholder={t("list.supplierTypePlaceholder")}
            clearable
            value={filtersValue.supplierType}
            onChange={(value) => {
              setFiltersValue({
                ...filtersValue,
                supplierType:
                  (value as SupplierTypeValidationType) ?? undefined,
              })
            }}
          />
          <Select
            w={150}
            placeholder={t("list.sortPlaceholder")}
            label={t("list.sortLabel")}
            leftSection={<IconSortDescending2 size={16} />}
            data={SUPPLIER_SORT_OPTIONS}
            onChange={(value) => {
              if (isSort(value)) {
                setSort((value as Sort) ?? "name-asc")
              }
            }}
            value={sort}
          />
          <CountrySelector
            value={filtersValue.country ?? null}
            countriesMapper={COUNTRIES}
            onChange={(value) =>
              setFiltersValue({
                ...filtersValue,
                country: (value as CountryIso3166) ?? undefined,
              })
            }
            selectCountries={suppliersCountries}
            searchable
            clearable
            placeholder={t("filter.country")}
          />
        </Group>
      </Stack>
      <DataFetchWrapper query={suppliersQuery}>
        {({ data }) =>
          data.suppliers.length > 0 ? (
            <SupplierMonitoring
              suppliers={data.suppliers}
              count={data.count}
              setPagination={setPagination}
              pagination={pagination}
              searchSupplier={searchSupplier}
              setSearchSupplier={setSearchSupplier}
              setFiltersValue={setFiltersValue}
              filtersValue={filtersValue}
              type={type}
            />
          ) : (
            <Container mt="10%">
              <InfoBox message={t("list.noSuppliers")} />
            </Container>
          )
        }
      </DataFetchWrapper>
    </Stack>
  )
}
